import _ from 'underscore';
import { SIGNUP_SUB_FLOW } from '@bingads-webui-campaign-react/shared-app-capi';
import {
  PMAX_LITE_WIZARD_STEP_PATHS, PREVIEW_TO_NON_PREVIEW_MAPPING, NON_PREVIEW_TO_PREVIEW_MAPPING, CAMPAIGN_TYPE, GOAL_TYPE,
} from './constants';

export const getFlow = ({
  isSmartPage,
  isSmartPageCustomDomainSupported,
  isSkipCampaign,
  isGoogleImport,
  isFacebookImport,
  isChinaSignup,
  psd2Country,
  isPreviewFlow,
  isCollectBusinessInfo,
  isTargetingInsideChina,
  isGoalBasedSignup,
  isAgenticSignup,
  selectedGoal = GOAL_TYPE.AWARENESS,
  selectedCampaignType = CAMPAIGN_TYPE.PMAX,
  showAllCampaignsAfterStepId,
} = {}) => {
  // Goal-based and agentic flows use the card-based CampaignCreationMethodSelection page;
  // all other flows keep the original CampaignCreationMethod page.
  const campaignCreationMethod = (isGoalBasedSignup || isAgenticSignup) && !isChinaSignup
    ? PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_CREATION_METHOD_SELECTION
    : PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_CREATION_METHOD;

  const initialStep = isCollectBusinessInfo ? [
    PMAX_LITE_WIZARD_STEP_PATHS.COLLECT_BUSINESS_INFO,
    campaignCreationMethod,
  ] : [
    campaignCreationMethod,
  ];

  const languageAndWebsiteStep = isCollectBusinessInfo ? [] : [
    PMAX_LITE_WIZARD_STEP_PATHS.LANGUAGE_COUNTRY,
    PMAX_LITE_WIZARD_STEP_PATHS.WEBSITE,
  ];

  const paymentStep = isPreviewFlow
    ? PMAX_LITE_WIZARD_STEP_PATHS.PREVIEW_PAYMENT
    : PMAX_LITE_WIZARD_STEP_PATHS.PAYMENT;

  const addFundsStep = isPreviewFlow
    ? PMAX_LITE_WIZARD_STEP_PATHS.PREVIEW_ADD_FUNDS
    : PMAX_LITE_WIZARD_STEP_PATHS.ADD_FUNDS;

  // psd2Country will be set if user choose prepay + credit card + pds2Country (Eg. Germany)
  const paymentSteps = psd2Country ? [
    paymentStep,
    addFundsStep,
  ] : [
    paymentStep,
  ];

  const accountStep = isPreviewFlow
    ? PMAX_LITE_WIZARD_STEP_PATHS.PREVIEW_ACCOUNT
    : PMAX_LITE_WIZARD_STEP_PATHS.ACCOUNT_DETAILS;

  const PmaxCampaignCreationSteps = [
    PMAX_LITE_WIZARD_STEP_PATHS.AD_ASSETS,
    PMAX_LITE_WIZARD_STEP_PATHS.TARGETING,
    PMAX_LITE_WIZARD_STEP_PATHS.BUDGET,
  ];

  let goalBasedSelectionStep = [];
  if (selectedGoal === GOAL_TYPE.CONVERSIONS) {
    goalBasedSelectionStep = [
      PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_GOAL_SELECTION,
      PMAX_LITE_WIZARD_STEP_PATHS.CATALOG_FEED_SELECTION,
      PMAX_LITE_WIZARD_STEP_PATHS.CHANNEL_SELECTION,
    ];
  } else if (selectedGoal === GOAL_TYPE.LEADS) {
    goalBasedSelectionStep = [
      PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_GOAL_SELECTION,
      PMAX_LITE_WIZARD_STEP_PATHS.CHANNEL_SELECTION,
    ];
  } else {
    goalBasedSelectionStep = [
      PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_GOAL_SELECTION,
    ];
  }

  if (showAllCampaignsAfterStepId && !_.isEmpty(goalBasedSelectionStep)) {
    const stepIndex = _.indexOf(goalBasedSelectionStep, showAllCampaignsAfterStepId);
    if (stepIndex !== -1) {
      goalBasedSelectionStep = _.first(goalBasedSelectionStep, stepIndex + 1)
        .concat(PMAX_LITE_WIZARD_STEP_PATHS.ALL_CAMPAIGN_TYPE_SELECTION);
    }
  }

  const SearchCampaignCreationSteps = [
    PMAX_LITE_WIZARD_STEP_PATHS.SEARCH_CAMPAIGN_SETTINGS,
    PMAX_LITE_WIZARD_STEP_PATHS.SEARCH_CAMPAIGN_KEYWORDS,
    PMAX_LITE_WIZARD_STEP_PATHS.SEARCH_CAMPAIGN_ADD_TEXT,
    PMAX_LITE_WIZARD_STEP_PATHS.SEARCH_CAMPAIGN_ADD_MORE_ASSETS,
    PMAX_LITE_WIZARD_STEP_PATHS.SEARCH_CAMPAIGN_BUDGET_AND_BIDS,
  ];

  let campaignCreationSteps = PmaxCampaignCreationSteps;

  if (isGoalBasedSignup || (isAgenticSignup && showAllCampaignsAfterStepId)) {
    if (selectedCampaignType === CAMPAIGN_TYPE.SEARCH) {
      campaignCreationSteps = SearchCampaignCreationSteps;
    } else if (selectedCampaignType === CAMPAIGN_TYPE.PMAX) {
      campaignCreationSteps = PmaxCampaignCreationSteps;
    } else {
      campaignCreationSteps = [];
    }
  }

  if (isPreviewFlow) {
    campaignCreationSteps = [PMAX_LITE_WIZARD_STEP_PATHS.PREVIEW_ASSETS];
  }

  if (isTargetingInsideChina && isCollectBusinessInfo) {
    return [
      PMAX_LITE_WIZARD_STEP_PATHS.COLLECT_BUSINESS_INFO,
      PMAX_LITE_WIZARD_STEP_PATHS.CHINA_DOMESTIC,
    ];
  }

  if (isFacebookImport) {
    return [
      ...initialStep,
      PMAX_LITE_WIZARD_STEP_PATHS.FACEBOOK_IMPORT,
    ];
  }

  if (isGoogleImport) {
    return [
      ...initialStep,
      PMAX_LITE_WIZARD_STEP_PATHS.GOOGLE_IMPORT,
    ];
  }

  if (isChinaSignup) {
    return [
      campaignCreationMethod,
      accountStep,
    ];
  }

  // collect business info step will never set isSmartPage to true
  if (isSmartPage) {
    if (isSmartPageCustomDomainSupported) {
      return [
        campaignCreationMethod,
        PMAX_LITE_WIZARD_STEP_PATHS.LANGUAGE_COUNTRY,
        PMAX_LITE_WIZARD_STEP_PATHS.WEBSITE,
        PMAX_LITE_WIZARD_STEP_PATHS.SMART_PAGE_SIGNUP,
        PMAX_LITE_WIZARD_STEP_PATHS.SMART_PAGE_EDITOR,
        PMAX_LITE_WIZARD_STEP_PATHS.SMART_PAGE_CUSTOM_DOMAIN,
        ...campaignCreationSteps,
        accountStep,
        ...paymentSteps,
      ];
    }
    return [
      campaignCreationMethod,
      PMAX_LITE_WIZARD_STEP_PATHS.LANGUAGE_COUNTRY,
      PMAX_LITE_WIZARD_STEP_PATHS.WEBSITE,
      PMAX_LITE_WIZARD_STEP_PATHS.SMART_PAGE_SIGNUP,
      PMAX_LITE_WIZARD_STEP_PATHS.SMART_PAGE_EDITOR,
      ...campaignCreationSteps,
      accountStep,
      ...paymentSteps,
    ];
  }

  if (isAgenticSignup && showAllCampaignsAfterStepId) {
    return [
      ...initialStep,
      PMAX_LITE_WIZARD_STEP_PATHS.ALL_CAMPAIGN_TYPE_SELECTION,
      ...campaignCreationSteps,
      accountStep,
      ...paymentSteps,
    ];
  }

  if (isAgenticSignup) {
    return [
      ...initialStep,
      ...(isSkipCampaign ? [] : [
        PMAX_LITE_WIZARD_STEP_PATHS.AGENTIC_ANALYZE,
        PMAX_LITE_WIZARD_STEP_PATHS.AGENTIC_SNAPSHOT_REVIEW,
        PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_DRAFT,
        PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_DRAFT_ASSETS,
        PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_DRAFT_BUDGET,
        PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_DRAFT_TARGETING,
      ]),
      accountStep,
      ...paymentSteps,
    ];
  }

  if (isGoalBasedSignup) {
    return [
      ...initialStep,
      ...(isSkipCampaign ? [] : goalBasedSelectionStep),
      ...(isSkipCampaign ? [] : campaignCreationSteps),
      accountStep,
      ...paymentSteps,
    ];
  }

  if (isSkipCampaign) {
    return [
      ...initialStep,
      ...languageAndWebsiteStep,
      PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_MODE,
      accountStep,
      ...paymentSteps,
    ];
  }

  // default is pmax campaign creation flow
  return [
    ...initialStep,
    ...languageAndWebsiteStep,
    PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_MODE,
    ...campaignCreationSteps,
    accountStep,
    ...paymentSteps,
    PMAX_LITE_WIZARD_STEP_PATHS.CONVERSION_UPSELL,
  ];
};

// The set of wizard steps that represent a "budget" step across the different signup flows.
// The regular / PMax flow uses BUDGET, Search campaigns use SEARCH_CAMPAIGN_BUDGET_AND_BIDS,
// and the agentic draft flow uses CAMPAIGN_DRAFT — its parent step, so navigating there
// re-runs the Campaign Draft loaders (refreshing the budget substep's currency list + amount)
// rather than deep-linking straight to the CAMPAIGN_DRAFT_BUDGET substep. The preview flow has
// no dedicated budget step (budget lives inside PREVIEW_ASSETS), so it is intentionally excluded.
export const BUDGET_STEP_IDS = [
  PMAX_LITE_WIZARD_STEP_PATHS.BUDGET,
  PMAX_LITE_WIZARD_STEP_PATHS.SEARCH_CAMPAIGN_BUDGET_AND_BIDS,
  PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_DRAFT,
];

// Returns the budget step id present in the given wizard flow, or undefined when the flow
// has no budget step (e.g. the skip-campaign or preview flows). currentFlow is the ordered
// array of step ids for the flow the user is actually in — read it from the wizard state via
// getCurrentFlow(wizardState) so the budget target always matches the live flow rather than
// being re-derived from flow factors.
export const getBudgetStepId = (currentFlow) => {
  if (!Array.isArray(currentFlow)) {
    return undefined;
  }
  return currentFlow.find(stepId => BUDGET_STEP_IDS.includes(stepId));
};

export const convertStepIdForPreviewFlow = (isPreviewFlow, currentStepId) => {
  if (isPreviewFlow) {
    return NON_PREVIEW_TO_PREVIEW_MAPPING[currentStepId] || currentStepId;
  }
  return PREVIEW_TO_NON_PREVIEW_MAPPING[currentStepId] || currentStepId;
};

export const getSubFlowFromCurrentFlow = (currentFlow) => {
  // currentFlow is an array of step ids
  if (currentFlow.includes(PMAX_LITE_WIZARD_STEP_PATHS.FACEBOOK_IMPORT)) {
    return SIGNUP_SUB_FLOW.FacebookImport;
  }
  if (currentFlow.includes(PMAX_LITE_WIZARD_STEP_PATHS.GOOGLE_IMPORT)) {
    return SIGNUP_SUB_FLOW.GoogleImport;
  }
  if (currentFlow.includes(PMAX_LITE_WIZARD_STEP_PATHS.SMART_PAGE_SIGNUP)) {
    return SIGNUP_SUB_FLOW.SmartPage;
  }
  if (!currentFlow.includes(PMAX_LITE_WIZARD_STEP_PATHS.AD_ASSETS)
    && !currentFlow.includes(PMAX_LITE_WIZARD_STEP_PATHS.PREVIEW_ASSETS)
    && !currentFlow.includes(PMAX_LITE_WIZARD_STEP_PATHS.SEARCH_CAMPAIGN_SETTINGS)
    && !currentFlow.includes(PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_DRAFT)
  ) {
    return SIGNUP_SUB_FLOW.SkipCampaign;
  }
  return SIGNUP_SUB_FLOW.CampaignCreation;
};
